<template>
  <section class="aip" :class="`aip--${variant}`">
    <div class="aip__tag"><CygnusIcon name="bolt" /> AI</div>
    <div class="aip__head">
      <h2>{{ title }}</h2>
      <span v-if="subtitle">{{ subtitle }}</span>
    </div>
    <div class="aip__box">
      <CygnusIcon name="bolt" class="aip__spark" />
      <textarea
        ref="input"
        :value="modelValue"
        :rows="rows"
        class="aip__input"
        :placeholder="placeholder"
        :disabled="generating"
        @input="$emit('update:modelValue', $event.target.value)"
        @keydown.meta.enter="emitGenerate"
        @keydown.ctrl.enter="emitGenerate"
      ></textarea>
      <button class="aip__go" :disabled="generating || !modelValue.trim()" @click="emitGenerate">
        <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="generating" />
        <CygnusIcon name="bolt" class="me-1" v-else />{{ buttonLabel }}
      </button>
    </div>
    <div v-if="examples.length" class="aip__chips">
      <span class="aip__try">Try:</span>
      <button
        v-for="ex in examples"
        :key="ex"
        type="button"
        class="aip__chip"
        :disabled="generating"
        @click="pickExample(ex)"
      ><CygnusIcon name="bolt" />{{ ex }}</button>
    </div>
    <p v-if="costText" class="aip__cost"><CygnusIcon name="bolt" /> {{ costText }}</p>
    <div v-if="error" class="alert alert-danger py-2 small mt-2 mb-0">{{ error }}</div>
    <div v-if="dividerText" class="aip__or"><span>{{ dividerText }}</span></div>
  </section>
</template>

<script>
/**
 * AiPromptPanel — the shared "describe it, don't configure it" prompt hero
 * (usability-refocus §5a). Extracted from the slice-3 PipelineWizard panel
 * (commit 515b003) so the Pipelines home and the wizard render ONE identical
 * component. Teal AI identity, never the indigo commit colour.
 *
 * The parent owns the prompt text (v-model) and the generate action (@generate)
 * plus its `generating` / `error` state — this component is purely the surface,
 * so both call sites keep their own API flow (the wizard POSTs then routes to
 * the draft; the home does the same). Generic service-seller examples only —
 * never web-agency decay signals (plan decision).
 *
 *   <AiPromptPanel v-model="promptText" :examples="examples"
 *     :generating="generating" :error="error" title="…" @generate="generate" />
 */
export default {
  name: 'AiPromptPanel',
  props: {
    modelValue: { type: String, default: '' },
    title:      { type: String, default: 'Describe the campaign you want…' },
    subtitle:   { type: String, default: 'We draft it — you review before anything runs.' },
    placeholder:{ type: String, default: "e.g. IT companies in Gloucestershire — who to find, where, and what you're offering…" },
    buttonLabel:{ type: String, default: 'Generate' },
    costText:   { type: String, default: '' },
    examples:   { type: Array,  default: () => [] },
    generating: { type: Boolean, default: false },
    error:      { type: String, default: '' },
    /** 'hero' (prominent, home) or 'inline' (compact, wizard). */
    variant:    { type: String, default: 'hero' },
    /** Optional trailing "or set it up manually below" divider (wizard). */
    dividerText:{ type: String, default: '' },
    rows:       { type: Number, default: 2 },
  },
  emits: ['update:modelValue', 'generate'],
  methods: {
    emitGenerate() {
      if (this.generating || !this.modelValue.trim()) return;
      this.$emit('generate');
    },
    pickExample(ex) {
      this.$emit('update:modelValue', ex);
      // Let the value settle before firing, so the parent reads the new prompt.
      this.$nextTick(() => this.$emit('generate'));
    },
  },
};
</script>

<style scoped>
.aip {
  position: relative; margin: 0 0 24px; padding: 20px 22px;
  background: linear-gradient(180deg, color-mix(in srgb, var(--m-accent-2) 12%, #fff), var(--m-bg-card) 66%);
  border: 1px solid color-mix(in srgb, var(--m-accent-2) 32%, #fff);
  border-radius: var(--m-radius-lg);
  box-shadow: var(--m-shadow-card);
}
.aip--inline { margin: 18px 0 8px; box-shadow: none; }
.aip__tag {
  position: absolute; top: 16px; right: 18px; background: var(--m-accent-2); color: #fff;
  font-weight: 700; font-size: 0.66rem; letter-spacing: 0.06em; border-radius: 999px; padding: 3px 10px;
}
.aip__head h2 { font-size: 1.12rem; font-weight: 750; margin: 0; color: var(--m-text-primary); }
.aip__head span { display: block; font-size: 0.82rem; color: var(--m-text-secondary); margin-top: 2px; }
.aip__box {
  display: flex; align-items: flex-start; gap: 10px; margin: 12px 0 10px; padding: 8px 8px 8px 14px;
  background: var(--m-bg-card); border: 1.5px solid color-mix(in srgb, var(--m-accent-2) 34%, #fff);
  border-radius: var(--m-radius);
}
.aip__spark { color: var(--m-accent-2); margin-top: 9px; }
.aip__input {
  flex: 1; border: none; resize: vertical; font-size: 0.92rem; background: transparent;
  color: var(--m-text-primary); outline: none; padding: 6px 4px; min-height: 40px;
}
.aip__go {
  flex: none; align-self: stretch; display: inline-flex; align-items: center; justify-content: center;
  background: var(--m-accent-2); color: #fff; border: 1px solid var(--m-accent-2);
  font-weight: 650; font-size: 0.85rem; border-radius: var(--m-radius); padding: 8px 16px; cursor: pointer;
  transition: background-color .12s;
}
.aip__go:hover:not(:disabled) { background: color-mix(in srgb, var(--m-accent-2) 82%, #000); }
.aip__go:disabled { opacity: .55; cursor: not-allowed; }
.aip__chips { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.aip__try { font-size: 0.76rem; color: var(--m-text-tertiary); }
.aip__chip {
  display: inline-flex; align-items: center; gap: 6px; font-size: 0.78rem; cursor: pointer;
  color: var(--m-accent-2); background: color-mix(in srgb, var(--m-accent-2) 10%, #fff);
  border: 1px solid color-mix(in srgb, var(--m-accent-2) 30%, #fff); border-radius: 999px; padding: 4px 11px;
}
.aip__chip:hover:not(:disabled) { background: color-mix(in srgb, var(--m-accent-2) 18%, #fff); }
.aip__chip:disabled { opacity: .55; cursor: not-allowed; }
.aip__cost { font-size: 0.74rem; color: var(--m-text-tertiary); margin: 10px 0 0; }
.aip__cost i { color: var(--m-accent-2); }
.aip__or { text-align: center; margin: 16px 0 0; border-top: 1px solid var(--m-border); }
.aip__or span {
  position: relative; top: -10px; background: var(--m-bg-content); padding: 0 12px;
  font-size: 0.74rem; color: var(--m-text-tertiary);
}
</style>
